import { c as _c } from "react/compiler-runtime";
import { useObservable } from '@legendapp/state/react';
import { useEffect, useRef } from 'react';
import { ObservableHint, batch } from '@legendapp/state';
import { headerState$ } from '@/components/SiteHeader/HeaderScrollAutoHide';
export type PageSubNavItem = {
  id: string;
  sectionContainerId: string;
  href: string;
  label: string;
  blockId?: number;
};
export type UseInitSubNavOptions = {
  items: PageSubNavItem[];
  viewportOffset?: number;
};
export const useInitSubNav = t0 => {
  const $ = _c(11);
  const {
    items,
    viewportOffset: t1
  } = t0;
  const viewportOffset = t1 === undefined ? 0.3 : t1;
  const containerRef = useRef(null);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      top: 0,
      left: 0,
      width: 0,
      enabled: false
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const pageSubNavState$ = useObservable({
    activeItem: () => {
      const activeSectionContainerId = pageSubNavState$.activeSectionContainerId.get();
      return items.find(item => item.sectionContainerId === activeSectionContainerId);
    },
    activeSectionContainerId: items[0]?.sectionContainerId,
    activeSectionLink: undefined as HTMLElement | undefined,
    indicatorState: t2
  });
  let t3;
  if ($[1] !== items || $[2] !== pageSubNavState$ || $[3] !== viewportOffset) {
    t3 = () => {
      if (!items.length) {
        return;
      }
      const handleScroll = () => {
        const scrollPosition = window.scrollY + window.innerHeight * viewportOffset;
        const newActive = items.find(item_0 => {
          const el = document.getElementById(item_0.sectionContainerId);
          if (!el) {
            return false;
          }
          const rect = el.getBoundingClientRect();
          const top = rect.top + window.scrollY;
          const bottom = top + el.offsetHeight;
          return scrollPosition >= top && scrollPosition < bottom;
        });
        if (!newActive) {
          return;
        }
        batch(() => {
          pageSubNavState$.activeSectionContainerId.set(newActive.sectionContainerId);
          if (!newActive?.href) {
            return;
          }
          if (window.location.hash !== newActive?.href) {
            window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}${newActive.href}`);
          }
          const sectionLink = containerRef.current?.querySelector(`a[href="${newActive.href}"]`);
          if (!sectionLink) {
            return;
          }
          pageSubNavState$.activeSectionLink.set(ObservableHint.opaque(sectionLink));
          const tabTop = (sectionLink.parentElement?.offsetTop || 0) + sectionLink.offsetTop || 0;
          const tabLeft = (sectionLink.parentElement?.offsetLeft || 0) + sectionLink.offsetLeft || 0;
          const itemWidth = sectionLink.clientWidth || 0;
          containerRef.current?.classList.add("has-indicator");
          pageSubNavState$.indicatorState.set({
            top: tabTop,
            left: tabLeft,
            width: itemWidth,
            enabled: itemWidth > 0
          });
          const index = items.indexOf(newActive);
          headerState$.hidden.set(index !== 0);
        });
      };
      window.addEventListener("scroll", handleScroll, {
        passive: true
      });
      handleScroll();
      return () => window.removeEventListener("scroll", handleScroll);
    };
    $[1] = items;
    $[2] = pageSubNavState$;
    $[3] = viewportOffset;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== items || $[6] !== pageSubNavState$ || $[7] !== viewportOffset) {
    t4 = [pageSubNavState$, items, viewportOffset];
    $[5] = items;
    $[6] = pageSubNavState$;
    $[7] = viewportOffset;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  useEffect(t3, t4);
  let t5;
  if ($[9] !== pageSubNavState$) {
    t5 = [pageSubNavState$, containerRef];
    $[9] = pageSubNavState$;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5 as const;
};
export type PageSubNavIndicatorState = ReturnType<typeof useInitSubNav>[0];